import { Ban, Eye, MoreVertical, Pencil, RotateCcw, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { deactivateClient, deleteClient, reactivateClient } from "@/features/clients/api";
import type { Client } from "@/features/clients/types";
import { ApiError } from "@/lib/api";

interface ClientRowActionsProps {
  client: Client;
  /** Called after a successful delete or status change so the parent can
   * refetch list + stats. */
  onChanged: () => void;
  /** Trigger button style — "ghost" (default) for list rows, "outline" to sit
   * alongside the outline buttons in the client-detail header. */
  triggerVariant?: "ghost" | "outline";
}

type Pending = "delete" | "deactivate" | "reactivate" | null;

/** The ⋮ menu on a clients-list row (and the client-detail header): View /
 * Edit / Deactivate|Reactivate / Delete. Destructive/status actions confirm
 * first. */
export function ClientRowActions({
  client,
  onChanged,
  triggerVariant = "ghost",
}: ClientRowActionsProps) {
  const navigate = useNavigate();
  // The confirm dialog is a sibling of the menu (not nested in its content) so
  // the closing menu's focus trap doesn't fight the opening dialog's.
  const [pending, setPending] = useState<Pending>(null);
  const [busy, setBusy] = useState(false);
  const isInactive = client.status === "INACTIVE";

  async function run(action: Exclude<Pending, null>) {
    setBusy(true);
    try {
      if (action === "delete") {
        await deleteClient(client.id);
        toast.success(`${client.name} deleted.`);
      } else if (action === "deactivate") {
        await deactivateClient(client.id);
        toast.success(`${client.name} deactivated.`);
      } else {
        await reactivateClient(client.id);
        toast.success(`${client.name} reactivated.`);
      }
      setPending(null);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : `Failed to ${action} client.`);
    } finally {
      setBusy(false);
    }
  }

  const confirmCopy: Record<
    Exclude<Pending, null>,
    { title: string; body: string; cta: string }
  > = {
    delete: {
      title: `Delete ${client.name}?`,
      body: "The client will be removed from your lists. Records tied to past filings are retained and can be restored by support.",
      cta: "Delete",
    },
    deactivate: {
      title: `Deactivate ${client.name}?`,
      body: "The client is marked inactive and drops off active views. You can reactivate them at any time.",
      cta: "Deactivate",
    },
    reactivate: {
      title: `Reactivate ${client.name}?`,
      body: "The client is marked active and returns to your active views.",
      cta: "Reactivate",
    },
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant={triggerVariant}
            size="icon-sm"
            aria-label={`Actions for ${client.name}`}
          >
            <MoreVertical className="size-[18px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onSelect={() => navigate(`/client/${client.id}/overview`)}>
            <Eye /> View
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={() => navigate(`/client/${client.id}/profile`)}>
            <Pencil /> Edit
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          {isInactive ? (
            <DropdownMenuItem onSelect={() => setPending("reactivate")}>
              <RotateCcw /> Reactivate
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onSelect={() => setPending("deactivate")}>
              <Ban /> Deactivate
            </DropdownMenuItem>
          )}
          <DropdownMenuItem variant="destructive" onSelect={() => setPending("delete")}>
            <Trash2 /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={pending !== null} onOpenChange={(open) => !open && setPending(null)}>
        <AlertDialogContent>
          {pending && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{confirmCopy[pending].title}</AlertDialogTitle>
                <AlertDialogDescription>{confirmCopy[pending].body}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
                <AlertDialogAction
                  disabled={busy}
                  onClick={(e) => {
                    e.preventDefault();
                    void run(pending);
                  }}
                >
                  {busy ? "Working…" : confirmCopy[pending].cta}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
